import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Grid, 
  Calendar, 
  BarChart3, 
  Kanban, 
  Filter, 
  Search, 
  Download,
  Users,
  MessageSquare,
  Bell,
  Settings,
  Plus,
  Save,
  RefreshCw,
  Brain,
  Table,
  Edit3
} from 'lucide-react';
import { EnhancedDataTable } from './EnhancedDataTable';
import { SpreadsheetGrid } from './SpreadsheetGrid';
import { SheetTabs } from './SheetTabs';
import { AIInsightsPanel } from './AIInsightsPanel';
import { FormulaEngine } from './FormulaEngine';
import { useSmartGridMemory } from '../../../hooks/useSmartGridMemory';
import { SmartGridCalendarView } from './SmartGridCalendarView';
import { SmartGridChartView } from './SmartGridChartView';
import { generateMockCalendarData, generateMockChartData, generateMockGridData, generateMockKanbanData, checkDemoMode } from './mockDataGenerators';

interface Sheet {
  id: string;
  name: string;
  data: any[];
  columns: any[];
}

interface ExcelLikeSmartGridProps {
  entityId?: string;
  className?: string;
}

export const ExcelLikeSmartGrid: React.FC<ExcelLikeSmartGridProps> = ({ 
  entityId = 'default-grid',
  className = '' 
}) => {
  const [activeView, setActiveView] = useState<'grid' | 'kanban' | 'calendar' | 'chart'>('grid');
  const [viewMode, setViewMode] = useState<'table' | 'spreadsheet'>('spreadsheet');
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [showCollaboration, setShowCollaboration] = useState(false);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  const [formulaInput, setFormulaInput] = useState('');
  const [showFormulaBar, setShowFormulaBar] = useState(true);
  const [demoMode, setDemoMode] = useState(checkDemoMode());
  const [mockCalendarData, setMockCalendarData] = useState<any[][]>([]);
  const [mockChartData, setMockChartData] = useState<any[][]>([]);
  const [mockGridData, setMockGridData] = useState<any[][]>([]);
  const [mockKanbanData, setMockKanbanData] = useState<any[][]>([]);
  
  const [sheets, setSheets] = useState<Sheet[]>([
    {
      id: 'sheet1',
      name: 'Sheet 1',
      data: [],
      columns: []
    }
  ]);
  const [activeSheetId, setActiveSheetId] = useState('sheet1');

  const {
    gridData,
    loading,
    error,
    saveData,
    loadData,
    updateCell,
    addRow,
    deleteRow
  } = useSmartGridMemory({ 
    entity: entityId, 
    sheetName: 'default' 
  });

  const formulaEngine = new FormulaEngine();
  const columns = gridData?.columns || [];
  const data = gridData?.rows || [];

  const activeSheet = sheets.find(sheet => sheet.id === activeSheetId) || sheets[0];

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (demoMode) {
      setMockCalendarData(generateMockCalendarData());
      setMockChartData(generateMockChartData());
      setMockGridData(generateMockGridData());
      setMockKanbanData(generateMockKanbanData());
    }
  }, [demoMode]);

  useEffect(() => {
    if (data.length > 0 && columns.length > 0) {
      setSheets(prev => prev.map(sheet => 
        sheet.id === activeSheetId 
          ? { ...sheet, data, columns }
          : sheet
      ));
    }
  }, [data, columns, activeSheetId]);

  const handleCellUpdate = useCallback((rowIndex: number, columnKey: string, value: any) => {
    if (data[rowIndex]) {
      updateCell(data[rowIndex].id, columnKey, value);
    }
  }, [updateCell, data]);

  const getViewData = (viewType: 'calendar' | 'chart' | 'grid' | 'kanban') => {
    if (demoMode) {
      switch (viewType) {
        case 'calendar': return mockCalendarData;
        case 'chart': return mockChartData;
        case 'kanban': return mockKanbanData;
        default: return mockGridData.length > 0 ? mockGridData : (activeSheet.data.length > 0 ? activeSheet.data : data);
      }
    }
    return activeSheet.data.length > 0 ? activeSheet.data : data;
  };

  const handleSpreadsheetCellChange = useCallback((rowIndex: number, colIndex: number, value: string) => {
    if (demoMode) {
      switch (activeView) {
        case 'calendar':
          setMockCalendarData(prev => {
            const updated = [...prev];
            if (!updated[rowIndex]) updated[rowIndex] = [];
            updated[rowIndex][colIndex] = value;
            return updated;
          });
          break;
        case 'chart':
          setMockChartData(prev => {
            const updated = [...prev];
            if (!updated[rowIndex]) updated[rowIndex] = [];
            updated[rowIndex][colIndex] = value;
            return updated;
          });
          break;
        case 'kanban':
          setMockKanbanData(prev => {
            const updated = [...prev];
            if (!updated[rowIndex]) updated[rowIndex] = [];
            updated[rowIndex][colIndex] = value;
            return updated;
          });
          break;
        default:
          setMockGridData(prev => {
            const updated = [...prev];
            if (!updated[rowIndex]) updated[rowIndex] = [];
            updated[rowIndex][colIndex] = value;
            return updated;
          });
      }
      return;
    }

    if (colIndex < columns.length && data[rowIndex]) {
      const columnKey = columns[colIndex]?.id;
      updateCell(data[rowIndex].id, columnKey, value);
    }
  }, [updateCell, columns, data, demoMode, activeView]);

  const handleCellSelect = useCallback((rowIndex: number, colIndex: number) => {
    setSelectedCell({ row: rowIndex, col: colIndex });
    
    if (rowIndex < data.length && colIndex < columns.length) {
      const columnKey = columns[colIndex]?.id;
      const cellValue = data[rowIndex]?.[columnKey] || '';
      setFormulaInput(cellValue);
      setShowFormulaBar(true);
    }
  }, [data, columns]);

  const handleRowSelect = useCallback((selectedRows: any[]) => {
  }, []);

  const handleExport = useCallback(async (format: 'csv' | 'xlsx' | 'pdf') => {
    try {
      console.log(`Exporting data as ${format}`);
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, []);

  const handleSheetAdd = useCallback(() => {
    const newSheetId = `sheet${sheets.length + 1}`;
    const newSheet: Sheet = {
      id: newSheetId,
      name: `Sheet ${sheets.length + 1}`,
      data: [],
      columns: []
    };
    setSheets(prev => [...prev, newSheet]);
    setActiveSheetId(newSheetId);
  }, [sheets.length]);

  const handleSheetDelete = useCallback((sheetId: string) => {
    if (sheets.length > 1) {
      setSheets(prev => prev.filter(sheet => sheet.id !== sheetId));
      if (activeSheetId === sheetId) {
        setActiveSheetId(sheets.find(sheet => sheet.id !== sheetId)?.id || sheets[0].id);
      }
    }
  }, [sheets, activeSheetId]);

  const handleSheetRename = useCallback((sheetId: string, newName: string) => {
    setSheets(prev => prev.map(sheet => 
      sheet.id === sheetId ? { ...sheet, name: newName } : sheet
    ));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5A7BC4]"></div>
        <span className="ml-2 text-gray-400">Loading SmartGrid...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-400">Error loading SmartGrid: {error}</div>
      </div>
    );
  }

  return (
    <div className={`excel-like-smartgrid h-full flex flex-col bg-gray-900 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-800 border-b border-gray-600">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveView('grid')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'grid' 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setActiveView('kanban')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'kanban' 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <Kanban size={20} />
            </button>
            <button
              onClick={() => setActiveView('calendar')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'calendar' 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <Calendar size={20} />
            </button>
            <button
              onClick={() => setActiveView('chart')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'chart' 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <BarChart3 size={20} />
            </button>
          </div>

          {activeView === 'grid' && (
            <div className="flex items-center gap-2 border-l border-gray-600 pl-4">
              <button
                onClick={() => setViewMode('spreadsheet')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'spreadsheet' 
                    ? 'bg-green-600 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title="Spreadsheet View"
              >
                <Edit3 size={20} />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-green-600 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title="Table View"
              >
                <Table size={20} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border-r border-gray-600 pr-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500/50 focus:outline-none"
              />
            </div>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
              <Filter size={20} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIInsights(!showAIInsights)}
              className={`p-2 rounded-lg transition-colors ${
                showAIInsights 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title="AI Insights"
            >
              <Brain size={20} />
            </button>
            <button
              onClick={() => setDemoMode(!demoMode)}
              className={`p-2 rounded-lg transition-colors ${
                demoMode 
                  ? 'bg-yellow-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title="Toggle Demo Mode"
            >
              <Brain size={20} />
            </button>
            <button
              onClick={() => setShowCollaboration(!showCollaboration)}
              className={`p-2 rounded-lg transition-colors ${
                showCollaboration 
                  ? 'bg-[#4267A1] text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <Users size={20} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
              <MessageSquare size={20} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
              <Bell size={20} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
              <Settings size={20} />
            </button>
          </div>

          <div className="flex items-center gap-2 border-l border-gray-600 pl-4">
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Download size={20} />
            </button>
            <button
              onClick={() => gridData && saveData(gridData)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Save size={20} />
            </button>
            <button
              onClick={loadData}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Formula Bar */}
      <AnimatePresence>
        {showFormulaBar && activeView === 'grid' && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gray-800 border-b border-gray-600 p-3"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400 font-medium">fx</span>
              <div className="text-sm text-gray-400 min-w-[60px]">
                {selectedCell ? `${String.fromCharCode(65 + selectedCell.col)}${selectedCell.row + 1}` : ''}
              </div>
              <input
                type="text"
                value={formulaInput}
                onChange={(e) => setFormulaInput(e.target.value)}
                placeholder="Enter formula or value..."
                className="flex-1 bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-[#4267A1]/50 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && selectedCell) {
                    handleSpreadsheetCellChange(selectedCell.row, selectedCell.col, formulaInput);
                  }
                }}
              />
              <button
                onClick={() => {
                  if (selectedCell) {
                    handleSpreadsheetCellChange(selectedCell.row, selectedCell.col, formulaInput);
                  }
                }}
                className="px-4 py-2 bg-[#4267A1] hover:bg-[#2E4F7A] text-white rounded transition-colors"
              >
                Apply
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1">
          <AnimatePresence mode="wait">
            {activeView === 'grid' && (
              <motion.div
                key="grid"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="h-full flex flex-col"
              >
                {viewMode === 'spreadsheet' ? (
                  <div className="flex-1 px-6 py-4">
                    <SpreadsheetGrid
                      data={getViewData('grid')}
                      columns={activeSheet.columns.length > 0 ? activeSheet.columns : columns}
                      onCellChange={handleSpreadsheetCellChange}
                      onCellSelect={handleCellSelect}
                      selectedCell={selectedCell}
                      formulaEngine={formulaEngine}
                      className="h-full"
                    />
                  </div>
                ) : (
                  <div className="flex-1">
                    <EnhancedDataTable
                      data={getViewData('grid')}
                      columns={activeSheet.columns.length > 0 ? activeSheet.columns : columns.map(col => ({
                        key: col.id,
                        label: col.header,
                        type: 'text',
                        sortable: col.sortable,
                        filterable: true,
                        editable: true
                      }))}
                      onCellEdit={handleCellUpdate}
                      onRowSelect={handleRowSelect}
                      className="h-full"
                    />
                  </div>
                )}
                
                <SheetTabs
                  sheets={sheets}
                  activeSheetId={activeSheetId}
                  onSheetSelect={setActiveSheetId}
                  onSheetAdd={handleSheetAdd}
                  onSheetDelete={handleSheetDelete}
                  onSheetRename={handleSheetRename}
                />
              </motion.div>
            )}

            {activeView === 'kanban' && (
              <motion.div
                key="kanban"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="h-full"
              >
                <EnhancedDataTable
                  data={getViewData('kanban')}
                  columns={activeSheet.columns.length > 0 ? activeSheet.columns : columns.map(col => ({
                    key: col.id,
                    label: col.header,
                    type: 'text',
                    sortable: col.sortable,
                    filterable: true,
                    editable: true
                  }))}
                  onCellEdit={handleCellUpdate}
                  onRowSelect={handleRowSelect}
                  className="h-full"
                />
              </motion.div>
            )}

            {activeView === 'calendar' && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="h-full"
              >
                <SmartGridCalendarView 
                  data={getViewData('calendar')}
                  onCellChange={handleSpreadsheetCellChange}
                  demoMode={demoMode}
                />
              </motion.div>
            )}

            {activeView === 'chart' && (
              <motion.div
                key="chart"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="h-full"
              >
                <SmartGridChartView 
                  data={getViewData('chart')}
                  onCellChange={handleSpreadsheetCellChange}
                  demoMode={demoMode}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* AI Insights Panel */}
        <AnimatePresence>
          {showAIInsights && (
            <AIInsightsPanel
              data={activeSheet.data.length > 0 ? activeSheet.data : data}
              columns={activeSheet.columns.length > 0 ? activeSheet.columns : columns}
              isVisible={showAIInsights}
              onClose={() => setShowAIInsights(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
